'use client';

import React from 'react';
import Play from '@src/components/Icons/Play';
import { VideoEmbedPlayerPropTypes } from '../VideoEmbedPlayer/VideoEmbedPlayer.types';
import Caption from '../Caption/Caption';

const VideoEmbedPlayer: React.FC<VideoEmbedPlayerPropTypes> = ({ className = '', data }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const hasThumbnail = Boolean(data?.thumbnail);
  const isContentful = data?.platform === 'Contentful';
  // Show media (video or iframe) once playing OR when there's no thumbnail to click
  const shouldShowMedia = isPlaying || !hasThumbnail;

  const handlePlay = () => setIsPlaying(true);

  // Append an autoplay flag so iframe embeds start playing immediately on click
  // instead of showing their own play button.
  const withAutoplay = (url: string) => {
    if (!url) return url;
    return `${url}${url.includes('?') ? '&' : '?'}autoplay=1`;
  };

  React.useEffect(() => {
    if (isPlaying && videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) playPromise.catch(() => {});
    }
  }, [isPlaying]);

  return (
    <div className={`my-[1.6rem] ${className}`}>
      <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
        {/* Media mounts underneath the thumbnail so it can start playing the moment play is clicked */}
        {isContentful
          ? // CONTENTFUL: use <video>
            shouldShowMedia && (
              <video
                ref={videoRef}
                className='absolute top-[0] left-[0] h-full w-full'
                src={data!.embedUrl}
                poster={data?.thumbnail?.url}
                loop
                muted
                playsInline
                controls={false}
                autoPlay={!hasThumbnail} // auto-start when there's no thumbnail
              >
                Sorry, your browser does not support embedded videos.
              </video>
            )
          : // OTHER PLATFORMS: use <iframe>
            shouldShowMedia && (
              <iframe
                className='absolute top-[0] left-[0] h-full w-full'
                src={withAutoplay(data!.embedUrl)}
                allowFullScreen
                allow='autoplay; fullscreen; encrypted-media; picture-in-picture'
                title='Video player'
              />
            )}

        {/* Thumbnail overlay — fades out on click, then lets clicks pass through to the player */}
        {hasThumbnail && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              background: `url(${data!.thumbnail!.url}) no-repeat center center`,
              zIndex: 1,
              opacity: isPlaying ? 0 : 1,
              pointerEvents: isPlaying ? 'none' : 'auto',
              transition: 'opacity 300ms ease',
            }}
            className='bg-cover'
            onClick={handlePlay}
            aria-hidden={isPlaying}
          >
            <Play className='text-primary absolute top-1/2 left-1/2 h-auto w-[10%] -translate-x-1/2 -translate-y-1/2' />
          </div>
        )}
      </div>

      {data?.bodyCopy && <Caption bodyCopy={data?.bodyCopy} />}
    </div>
  );
};

export default VideoEmbedPlayer;
